import { Suspense, lazy, startTransition, useEffect, useMemo, useRef, useState } from "react";

const TONE_BG = { paper: "bg-paper", mist: "bg-mist", ink: "bg-ink" };

// Every mounted <Deferred> registers here, in page order, so navigation can mount a run of
// sections synchronously before scrolling to one of them.
const registry = new Map(); // id -> { order, load, show }
const chunks = new Map(); // load -> import() promise
const lazies = new Map(); // load -> React.lazy component

const loadChunk = (load) => {
  if (!chunks.has(load)) chunks.set(load, load());
  return chunks.get(load);
};

const lazyFor = (load) => {
  if (!lazies.has(load)) lazies.set(load, lazy(load));
  return lazies.get(load);
};

// Resolves after the window has loaded and the main thread has an idle slot, so the first
// paint (nav + hero) is never competing with below-the-fold code.
let ready;
function whenReady() {
  if (!ready) {
    ready = new Promise((resolve) => {
      const idle = () => {
        if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(() => resolve(), { timeout: 1200 });
        else setTimeout(resolve, 250);
      };
      if (document.readyState === "complete") idle();
      else {
        let done = false;
        const go = () => {
          if (done) return;
          done = true;
          idle();
        };
        window.addEventListener("load", go, { once: true });
        setTimeout(go, 2500); // never wait on slow third-party scripts
      }
    });
  }
  return ready;
}

// Resolves once no placeholder with order <= limit is left in the DOM (or after ~2.5s).
const settled = (limit) =>
  new Promise((resolve) => {
    const started = performance.now();
    const tick = () => {
      const pending = [...registry.values()].some((r) => {
        const el = r.order <= limit && document.getElementById(r.id);
        return el && el.hasAttribute("data-placeholder");
      });
      if (!pending || performance.now() - started > 2500) requestAnimationFrame(() => resolve());
      else requestAnimationFrame(tick);
    };
    tick();
  });

// Mount every deferred section from the top of the page through `id`, wait for layout, then
// resolve. Used before programmatic scrolls so the target does not move while sections above it
// are still filling in. Ids that are not deferred (the hero) resolve immediately.
export async function ensureMounted(id) {
  const target = registry.get(id);
  if (!target) return;
  const run = [...registry.values()].filter((r) => r.order <= target.order && !r.shown);
  if (!run.length) return;
  await Promise.all(run.map((r) => loadChunk(r.load)));
  run.forEach((r) => r.show(false));
  await settled(target.order);
}

// Sections above a newly visible one mount too, so nothing above the viewport changes height later.
function mountThrough(order) {
  registry.forEach((r) => {
    if (r.order <= order && !r.shown) r.show(true);
  });
}

/**
 * Code-split section that mounts when it comes within ~600px of the viewport (after the page
 * has loaded and the main thread is idle). Until then a same-id placeholder keeps the scroll
 * length, anchors, scrollspy and keyboard stops working.
 */
export function Deferred({ id, order, load, tone = "paper", minHeight = "100vh" }) {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  const C = useMemo(() => lazyFor(load), [load]);

  useEffect(() => {
    const entry = {
      id,
      order,
      load,
      shown: false,
      show: (transition) => {
        entry.shown = true;
        if (transition) startTransition(() => setShow(true));
        else setShow(true);
      },
    };
    registry.set(id, entry);
    return () => {
      if (registry.get(id) === entry) registry.delete(id);
    };
  }, [id, order, load]);

  useEffect(() => {
    if (show) return undefined;
    let io;
    let cancelled = false;
    whenReady().then(() => {
      if (cancelled) return;
      const el = ref.current;
      const entry = registry.get(id);
      if (!el || !entry || entry.shown) return;
      if (typeof IntersectionObserver === "undefined") {
        entry.show(false);
        return;
      }
      io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            loadChunk(load).then(() => mountThrough(order));
          }
        },
        { rootMargin: "600px 0px" },
      );
      io.observe(el);
    });
    return () => {
      cancelled = true;
      if (io) io.disconnect();
    };
  }, [show, id, order, load]);

  const placeholder = (withRef) => (
    <div
      ref={withRef ? ref : undefined}
      id={id}
      data-stop=""
      data-placeholder=""
      aria-hidden="true"
      className={TONE_BG[tone] || TONE_BG.paper}
      style={{ minHeight }}
    />
  );

  return show ? <Suspense fallback={placeholder(false)}>{<C />}</Suspense> : placeholder(true);
}
